import { useQuery } from '@tanstack/react-query';
import { ERC20_ABI } from '../config/oft';
import { getCctpConfig } from '../config/cctp';
import { getSourceChainPublicClient } from '../utils/getSourceChainPublicClient';

export interface CctpAllowanceData {
  /** Raw USDC allowance as BigInt */
  allowance: bigint;
  /** Whether the current allowance is sufficient for the given amount */
  isApproved: (amount: bigint) => boolean;
}

/**
 * Checks the USDC allowance for the CCTP TokenMessengerV2 on the given chain.
 *
 * Uses a chain-specific publicClient (NOT the wagmi-connected wallet chain)
 * so it works regardless of which chain the wallet is currently on.
 *
 * Polls every 3 seconds so the UI reacts quickly after an approval tx confirms.
 *
 * @param chainId  - EVM chain ID where the burn will happen
 * @param address  - Owner address to check allowance for
 */
export function useCctpAllowance(
  chainId: number | undefined,
  address: `0x${string}` | undefined,
) {
  return useQuery<CctpAllowanceData>({
    queryKey: ['cctpAllowance', chainId, address],
    queryFn: async () => {
      if (!chainId || !address) {
        throw new Error('chainId and address are required');
      }

      const cctpConfig = getCctpConfig(chainId);
      const publicClient = getSourceChainPublicClient(chainId);

      const allowance = await publicClient.readContract({
        address: cctpConfig.usdcAddress,
        abi: ERC20_ABI,
        functionName: 'allowance',
        args: [address, cctpConfig.tokenMessenger],
      });

      return {
        allowance,
        isApproved: (amount: bigint) => allowance >= amount,
      };
    },
    enabled: !!chainId && !!address,
    staleTime: 3_000,
    refetchInterval: 3_000,
  });
}
